import React, { useState, useEffect } from 'react';
import { ArrowRight, PieChart, ShieldCheck, TrendingUp, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const FinanceLanding = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // 监听滚动以处理导航栏样式
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-[#FDFDFD] text-slate-800 font-sans selection:bg-slate-200">

            {/* --- Navigation --- */}
            <nav
                className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/80 backdrop-blur-md py-4 shadow-sm' : 'bg-transparent py-6'
                    }`}
            >
                <div className="container mx-auto px-6 flex justify-between items-center">
                    {/* Logo */}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center">
                            <div className="w-3 h-3 bg-white rounded-full"></div>
                        </div>
                        <span className="text-xl font-bold tracking-tight text-slate-900">上海繁昕财务咨询有限公司</span>
                    </div>

                    {/* Desktop Links - 已修复 href="#" 为 href="/" */}
                    <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-500">
                        <Link to="/services" className="hover:text-slate-900 transition-colors">服务</Link>
                        <a href="/" className="hover:text-slate-900 transition-colors">关于我们</a>
                        <Link to="/contact" className="px-5 py-2.5 bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-transform hover:scale-105 active:scale-95">
                            咨询
                        </Link>
                    </div>

                    {/* Mobile Toggle */}
                    <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                        {mobileMenuOpen ? <X /> : <Menu />}
                    </button>
                </div>
            </nav>

            {/* --- Hero Section --- */}
            <header className="pt-32 pb-20 px-6 relative overflow-hidden">
                <div className="container mx-auto max-w-6xl">
                    <div className="flex flex-col md:flex-row items-center gap-12">

                        {/* Left Content */}
                        <div className="flex-1 z-10 animate-fade-in-up">
                            <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold tracking-wide">
                                持牌经营 · 专业合规 · 技术驱动
                            </div>
                            <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight text-slate-900 mb-6">
                                专注企业财税 <br />
                                <span className="text-slate-400">构建运营闭环</span>
                            </h1>
                            <p className="text-lg text-slate-500 mb-8 max-w-lg leading-relaxed">
                                经相关部门批准的专业代理记账机构。我们提供从财务合规、商务秘书到技术支持的全方位解决方案，让您专注业务核心。
                            </p>
                            <div className="flex gap-4">
                                <button className="group flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 transition-all hover:shadow-lg hover:-translate-y-1">
                                    现在开始
                                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                                <Link to="/services" className="px-8 py-4 bg-slate-100 text-slate-700 rounded-2xl hover:bg-slate-200 transition-colors">
                                    了解更多
                                </Link>
                            </div>
                        </div>

                        {/* Right Visual (Abstract Blocks) */}
                        <div className="flex-1 relative w-full h-[400px] md:h-[500px]">
                            {/* 纯色块组合 - 低饱和度 */}
                            <div className="absolute top-0 right-0 w-4/5 h-full bg-[#E8ECEF] rounded-[3rem] animate-float-slow"></div>
                            <div className="absolute bottom-10 left-10 w-3/5 h-3/5 bg-[#D2D8DF] rounded-[2.5rem] shadow-xl animate-float-delayed flex items-center justify-center">
                                <div className="text-center">
                                    <TrendingUp size={24} />
                                    <p className="text-4xl font-bold text-slate-800 mb-1">24%</p>
                                    <p className="text-sm text-slate-600">年度平均增长</p>
                                </div>
                            </div>
                            {/* 装饰性小圆点 */}
                            <div className="absolute top-20 left-20 w-4 h-4 bg-slate-800 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </header>

            {/* --- Features Section --- */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">核心服务优势</h2>
                        <div className="w-20 h-1.5 bg-slate-200 rounded-full"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Feature Cards */}
                        {[
                            {
                                icon: <ShieldCheck size={24} />, // 这里的图标建议引入 ShieldCheck
                                title: "代理记账与合规",
                                desc: "正规持牌机构。提供标准化代理记账、纳税申报及财务合规咨询，规避经营风险。",
                                bg: "bg-[#F3F6F8]"
                            },
                            {
                                icon: <TrendingUp size={24} />, // 引入 TrendingUp
                                title: "商务咨询与秘书",
                                desc: "提供社会经济咨询、商务秘书服务及企业信息规划，协助初创企业快速落地与高效运营。",
                                bg: "bg-[#EEF2F6]"
                            },
                            {
                                icon: <PieChart size={24} />, // 这里的图标可以换成 Cpu 或 Laptop (代表技术/产品)
                                title: "技术与物资支持",
                                desc: "从技术开发咨询到办公电子设备的一站式采买支持，解决企业通过“财税+科技”转型的后顾之忧。",
                                bg: "bg-[#EBF1F5]"
                            }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className={`${item.bg} p-10 rounded-[2rem] hover:scale-[1.02] transition-transform duration-300 cursor-pointer group`}
                            >
                                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 text-slate-800 shadow-sm group-hover:scale-110 transition-transform">
                                    {item.icon}
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-500 leading-relaxed">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Data/Trust Section --- */}
            <section className="py-20 px-6 bg-slate-900 text-white rounded-t-[3rem] mt-10">
                <div className="container mx-auto max-w-6xl flex flex-col md:flex-row justify-between items-center gap-10">
                    <div className="md:w-1/2">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">数据不会说谎</h2>
                        <p className="text-slate-400 text-lg">
                            我们是<span className='font-bold italic'>上海繁昕财务咨询有限公司</span>，在过去十年中，我们协助超过 1000 位高净值客户实现了财富自由目标。
                        </p>
                    </div>
                    <div className="md:w-1/2 grid grid-cols-2 gap-8">
                        <div>
                            <p className="text-4xl font-bold text-[#A8B4C2] mb-2">1000+</p>
                            <p className="text-sm text-slate-500 uppercase tracking-wider">服务客户</p>
                        </div>
                        <div>
                            <p className="text-4xl font-bold text-[#A8B4C2] mb-2">￥20M+</p>
                            <p className="text-sm text-slate-500 uppercase tracking-wider">管理资产</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Footer --- */}
            <footer className="bg-slate-950 text-slate-600 py-12 px-6">

                <div className="container mx-auto max-w-6xl flex flex-col md:flex-row justify-between items-center text-sm">
                    <p>&copy; 2025 上海繁昕财务咨询有限公司. All rights reserved.</p>
                    {/* Footer Links - 已修复 href="#" 为 href="/" */}
                    <div className="flex gap-6 mt-4 md:mt-0">
                        <a href="/" className="hover:text-slate-400">隐私条款</a>
                        <Link to="/contact" className="hover:text-slate-400">联系我们</Link>
                    </div>
                </div>
            </footer>

            {/* --- Simple CSS for Custom Animations --- */}
            <style>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 5s ease-in-out infinite;
          animation-delay: 1s;
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
        </div>
    );
};

export default FinanceLanding;